import useHelpTarget from '../hooks/useHelpTarget';

/**
 * ImproveTranscriptCard
 *
 * Replaces the two stacked "Generate Full Transcript" / "Complete
 * Full Transcription" buttons with a single card that exposes the
 * two upgrade paths side-by-side:
 *
 *   📈 Full coverage (Google) — re-transcribe the whole file via
 *      Cloud Speech / Gemini. Stays in Google Workloads. ~$0.02/min.
 *
 *   ✨ AI transcript (AssemblyAI) — speaker labels, chapters,
 *      entity detection. Audio leaves Workloads. ~$0.85/min.
 *
 * Hide rules:
 *   - Full coverage hidden when extraction_coverage >= 100 OR tier === 'deep'
 *     (Deep already runs FullMediaStrategy; the coverage_percentage that
 *     stays <100 reflects speech density of the recording, not extraction
 *     completeness — see media_full_extraction stamp in ensure_full_extraction.)
 *   - AI transcript hidden when merged_transcript already exists
 *   - Whole card hidden when both are done
 *
 * Disable rule:
 *   - Both buttons disable while EITHER upgrade is queued/processing
 *     so the user can't accidentally double-bill.
 */
export default function ImproveTranscriptCard({
  isMedia,
  extractionCoverage,
  hasMerged,
  tier,                // 'skim' | 'surface' | 'deep' | undefined
  upgradeJobStatus,    // 'queued' | 'processing' | 'completed' | 'failed' | null
  fullTranscribing,    // bool — Google full-coverage in flight
  transcribeMsg,       // string status message from the Google flow
  whisperMsg,          // string status message from any AAI inline updates
  onRunFullCoverage,
  onGenerateAITranscript,
}) {
  const helpRef = useHelpTarget({
    slug: 'transcripts-and-speaker-labels',
    label: 'Improve transcript options',
    question: "What's the difference between Full coverage and AI transcript?",
  });
  if (!isMedia) return null;

  const fullCoverageDone = (extractionCoverage ?? 0) >= 100 || tier === 'deep';
  const aiDone = !!hasMerged;
  if (fullCoverageDone && aiDone) return null;

  const upgradeProcessing = upgradeJobStatus === 'processing' || upgradeJobStatus === 'queued';
  const anyRunning = upgradeProcessing || fullTranscribing;

  return (
    <div className="improve-card" ref={helpRef}>
      <div className="improve-card-title">Improve transcript</div>
      <div className="improve-card-row">
        {!fullCoverageDone && (
          <div className="improve-option">
            <div className="improve-option-head">
              <span className="improve-option-icon">📈</span>
              <span className="improve-option-name">Full coverage (Google)</span>
            </div>
            <div className="improve-option-desc">
              Re-transcribe the whole file with Cloud Speech / Gemini.
              No speakers or chapters, but every word is covered.
            </div>
            <div className="improve-option-meta">
              <span className="improve-cost">~$0.02/min</span>
              <span className="improve-badge improve-badge--safe">✓ Stays in Workloads</span>
            </div>
            <button
              className="btn btn-sm"
              onClick={onRunFullCoverage}
              disabled={anyRunning}
              type="button"
            >
              {fullTranscribing ? '📈 Running…' : '📈 Run full coverage'}
            </button>
          </div>
        )}
        {!aiDone && (
          <div className="improve-option improve-option--ai">
            <div className="improve-option-head">
              <span className="improve-option-icon">✨</span>
              <span className="improve-option-name">AI transcript (AssemblyAI)</span>
            </div>
            <div className="improve-option-desc">
              Speaker labels, auto-chapters, entity detection.
              Higher accuracy on noisy audio.
            </div>
            <div className="improve-option-meta">
              <span className="improve-cost">~$0.85/min</span>
              <span className="improve-badge improve-badge--warn">⚠ Leaves Workloads</span>
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={onGenerateAITranscript}
              disabled={anyRunning}
              type="button"
            >
              {upgradeProcessing ? '✨ Generating…' : '✨ Generate AI transcript'}
            </button>
          </div>
        )}
      </div>
      {anyRunning && (
        <div className="improve-status improve-status--running">
          Queued — track progress in the banner above.
        </div>
      )}
      {transcribeMsg && !anyRunning && (
        <div className="improve-status">{transcribeMsg}</div>
      )}
      {whisperMsg && !anyRunning && (
        <div className="improve-status">{whisperMsg}</div>
      )}
    </div>
  );
}
